import { Spinner } from '@flex-design-system/fx';
import { styled } from '@flex-design-system/stitches-react';
import { COOKIE_KEY_LOCALE } from '@flex-packages/i18n-base';
import { useFlexRouter } from '@flex-packages/router';
import { CookieManager } from '@flex-services-platform/authentication/react';
import { useEffect, useState, type PropsWithChildren } from 'react';

import languageDetector, {
  getLanguageCode,
} from '../../utils/languageDetector';

interface WithLanguageDetectionProps extends PropsWithChildren {
  useLoader?: boolean;
}

export const WithLanguageDetection = ({
  children,
  useLoader = false,
}: WithLanguageDetectionProps) => {
  const { updateQueryParams } = useFlexRouter();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const language = languageDetector.detect()!;
    const languageCodeFromBrowser = getLanguageCode(language);

    const cookie = CookieManager.getInstance({ secure: true });
    const languageCodeFromCookie = cookie.get(COOKIE_KEY_LOCALE);

    if (languageCodeFromCookie !== languageCodeFromBrowser) {
      cookie.set(COOKIE_KEY_LOCALE, languageCodeFromBrowser);
      languageDetector.cache?.(languageCodeFromBrowser);

      updateQueryParams(
        prev => ({
          ...prev,
          locale: languageCodeFromBrowser,
        }),
        {
          mode: 'replace',
        }
      );
    }

    if (useLoader) {
      setTimeout(() => setIsLoading(false), 300);
    }
  }, [useLoader, updateQueryParams]);

  if (useLoader) {
    return isLoading ? <CenteredSpinner /> : <>{children}</>;
  }

  return <>{children}</>;
};

const CenteredSpinner = styled(Spinner, {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
});
